import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { rbac } from '@dha-nexus/shared';
import { personaKeyFromRoles, primaryRoleLabel } from '../../lib/personaKey.js';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { wrapGlossaryTerms } from '../../components/primitives/GlossaryTerm.jsx';
import { IconClose } from '../../components/icons.jsx';
import './aboutThisPage.css';

// "About this page" contextual drawer (WP5 SME onboarding).
//
// Opened by <AboutThisPageButton>, this portal drawer explains the current
// screen in plain language: what it is for, why it matters for the signed-in
// persona, and the first things to do. Copy is resolved from the generated
// bundle apps/web/src/content/aboutPageContent.json, which is LAZY-LOADED via a
// dynamic import on first open so the (larger) content bundle stays out of the
// main app chunk and off the TopBar's synchronous import graph.

// Dispatch the shared tour-start event consumed by the parallel tour engine.
function startTour(tourId) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('prime:start-tour', { detail: { tourId } }));
}

// Resolve the pathname to a content key. Exact key wins; otherwise the first
// param-pattern key (e.g. '/projects/:projectId') that fully matches is used.
export function resolveAboutKey(content, pathname) {
  if (!content || typeof content !== 'object' || !pathname) return null;
  if (Object.prototype.hasOwnProperty.call(content, pathname)) return pathname;
  for (const key of Object.keys(content)) {
    if (!key.includes(':')) continue;
    if (matchPath({ path: key, end: true }, pathname)) return key;
  }
  return null;
}

export function AboutThisPagePanel({ onClose, returnFocusRef }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { roles } = useCurrentUser();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const panelRef = useRef(null);
  const closeBtnRef = useRef(null);

  const personaKey = personaKeyFromRoles(roles);
  // Lead with the account's ACTUAL role; the persona-bucket label is only a
  // fallback for role-less sessions (DEF-002).
  const personaLabel =
    primaryRoleLabel(roles) || (personaKey ? rbac.PERSONA_DISPLAY[personaKey]?.label : null);

  // Lazy-load the generated content bundle exactly once, on mount (= first open,
  // because the panel is conditionally mounted). Kept as a dynamic import so it
  // is emitted as its own chunk rather than pulled into the TopBar bundle.
  useEffect(() => {
    let alive = true;
    import('../../content/aboutPageContent.json')
      .then((mod) => {
        if (!alive) return;
        setContent(mod.default || mod);
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setContent({});
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  // Focus the close button on open; restore focus to the opener on close.
  // The opener node is captured at mount so the cleanup does not read a ref that
  // may have changed (it is the persistent TopBar About button either way).
  useEffect(() => {
    const opener = returnFocusRef?.current;
    closeBtnRef.current?.focus();
    return () => {
      opener?.focus?.();
    };
  }, [returnFocusRef]);

  // Esc closes.
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const aboutKey = content ? resolveAboutKey(content, location.pathname) : null;
  const entry = aboutKey ? content[aboutKey] : null;
  const hasEntry = Boolean(entry && (entry.whatFor || entry.whyForYou || entry.firstActions?.length));

  const body = (
    <div
      className="about-panel-backdrop"
      onClick={onClose}
      role="presentation"
      data-testid="about-panel-backdrop"
    >
      <aside
        className="about-panel"
        role="dialog"
        aria-modal="true"
        aria-label="About this page"
        data-testid="about-panel"
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="about-panel-header">
          <div>
            <p className="about-panel-eyebrow">About this page</p>
            <h2 className="about-panel-title">What this page is for</h2>
          </div>
          <button
            type="button"
            className="about-panel-close"
            onClick={onClose}
            aria-label="Close about this page"
            ref={closeBtnRef}
            data-testid="about-panel-close"
          >
            <IconClose size={18} />
          </button>
        </header>

        <div className="about-panel-body">
          {loading ? (
            <p className="about-panel-loading" data-testid="about-panel-loading">
              Loading…
            </p>
          ) : hasEntry ? (
            <>
              {entry.whatFor ? (
                <section className="about-section" data-testid="about-section-whatfor">
                  <h3 className="about-section-title">What this page is for</h3>
                  <p className="about-section-text">{wrapGlossaryTerms(entry.whatFor)}</p>
                </section>
              ) : null}

              {entry.whyForYou ? (
                <section className="about-section" data-testid="about-section-whyforyou">
                  <h3 className="about-section-title">Why it matters for you</h3>
                  <p className="about-section-text">
                    {personaLabel ? (
                      <span className="about-persona-lead" data-testid="about-persona-lead">
                        {personaLabel}:{' '}
                      </span>
                    ) : null}
                    {wrapGlossaryTerms(entry.whyForYou)}
                  </p>
                </section>
              ) : null}

              {entry.firstActions?.length ? (
                <section className="about-section" data-testid="about-section-firstactions">
                  <h3 className="about-section-title">Do these first</h3>
                  <ol className="about-actions">
                    {entry.firstActions.map((action, i) => (
                      <li key={i} className="about-action">
                        {wrapGlossaryTerms(action)}
                      </li>
                    ))}
                  </ol>
                </section>
              ) : null}
            </>
          ) : (
            <div className="about-panel-fallback" data-testid="about-panel-fallback">
              <p className="about-section-text">
                This screen doesn&rsquo;t have a guide yet. You can still get oriented from Help
                &amp; getting started or take the two-minute tour below.
              </p>
            </div>
          )}
        </div>

        <footer className="about-panel-footer">
          <button
            type="button"
            className="about-panel-footer-link"
            data-testid="about-open-help"
            onClick={() => {
              navigate('/help');
              onClose();
            }}
          >
            Open Help &amp; getting started
          </button>
          <button
            type="button"
            className="about-panel-footer-link about-panel-tour"
            data-testid="about-start-tour"
            onClick={() => {
              startTour('orientation');
              onClose();
            }}
          >
            Take the 2-minute tour
          </button>
        </footer>
      </aside>
    </div>
  );

  if (typeof document === 'undefined') return body;
  return createPortal(body, document.body);
}
